/**
 * Organization entitlements.
 *
 * Gates what a tenant can see and do, independently of what a *user* may do.
 * `crm-users` permissions answer "may this person do X"; entitlements answer
 * "is X part of this organization's plan at all". Both must pass.
 *
 * Rollout note: every existing organization resolves to `['*']` because the
 * field is absent on their document — see `resolveEntitlements`. Nothing is
 * gated until a real feature list is written onto an org, so this ships dark.
 */

/** Wildcard grant. An org holding this passes every entitlement check. */
export const ENTITLEMENT_ALL = '*' as const;

/**
 * Known feature keys. Deliberately a `string` union widened with `(string & {})`
 * so a plan can carry a key that predates this build without failing to type —
 * unknown keys simply never match a `@RequiresEntitlement` site.
 *
 * Paywalls in force today: `helpdesk.triage` (Triage Agent). `helpdesk` is the
 * Support section paywall — declared so Stripe can attach it, not yet enforced.
 * Legacy lookup keys (`settings`, `catalog`, …) may still appear on billed
 * orgs; they are no longer checked.
 */
export type Entitlement =
  | 'helpdesk'
  | 'helpdesk.triage'
  | 'orders'
  | 'patients'
  | 'clinical'
  | 'catalog'
  | 'discounts'
  | 'marketing'
  | 'automations'
  | 'insights'
  | 'settings'
  | 'api-keys'
  // eslint-disable-next-line @typescript-eslint/ban-types
  | (string & {});

/** Plan features the billing page lists. Only these are (or will be) paywalled. */
export const KNOWN_ENTITLEMENTS: readonly Entitlement[] = [
  'helpdesk',
  'helpdesk.triage',
];

export interface OrganizationEntitlements {
  /**
   * Plan identifier. Informational — `features` is what is actually enforced.
   * Every organization is `'full'` at launch.
   */
  plan: string;
  /**
   * Granted feature keys, or `['*']` for everything.
   *
   * An **empty array means "nothing granted"**, not "unset" — that is the state
   * of an org whose Stripe subscription has been canceled. "Unset" is the
   * absence of the whole `entitlements` block; see `resolveEntitlements`.
   */
  features: Entitlement[];
  /**
   * Where `features` came from. `'stripe'` is written by the billing webhook
   * from the customer's active entitlement summary; `'manual'` is an operator
   * override that the webhook must not clobber.
   */
  source?: 'stripe' | 'manual';
  /**
   * `created` of the Stripe event that last wrote this block.
   *
   * Watermark for out-of-order delivery: Stripe does not guarantee event
   * ordering and retries failures, so an older summary arriving late must not
   * overwrite newer state — that would restore features a customer no longer
   * has. Writes are rejected unless strictly newer. Independent of
   * `OrganizationBilling.lastEventAt`: the two blocks are written by different
   * event streams and must not share a watermark.
   */
  lastEventAt?: Date | string;
  updatedAt?: Date | string;
}

/** Shape accepted by `resolveEntitlements` — anything carrying the field. */
export interface HasEntitlements {
  entitlements?: OrganizationEntitlements | null;
}

/**
 * Resolve an organization's granted features.
 *
 * Precedence:
 *  - No `entitlements` block at all → `['*']`. This is the "not yet onboarded
 *    to billing" state and covers every organization that predates this
 *    feature, so no migration is required and nobody loses access.
 *  - A block with a `features` array → that array, **including when it is
 *    empty**. An empty array is a real answer: it is what an org whose Stripe
 *    subscription was canceled looks like, and collapsing it back to `['*']`
 *    would hand full access to a lapsed tenant.
 *
 * Only the *whole block being absent* means "ungated".
 */
export function resolveEntitlements(
  org: HasEntitlements | null | undefined
): Entitlement[] {
  const entitlements = org?.entitlements;
  if (!entitlements) return [ENTITLEMENT_ALL];

  const features = entitlements.features;
  // A malformed block (missing/!array `features`) is treated as unset rather
  // than as a denial — bad data should not take a tenant offline.
  if (!Array.isArray(features)) return [ENTITLEMENT_ALL];

  return features;
}

/**
 * True when `features` grants `required`. `['*']` grants everything.
 *
 * `null`/`undefined` means "unresolved" and grants everything; an empty array
 * means "explicitly nothing" and grants nothing. Keep these distinct — see
 * `resolveEntitlements`.
 */
export function hasEntitlement(
  features: readonly Entitlement[] | null | undefined,
  required: Entitlement
): boolean {
  if (!Array.isArray(features)) return true;
  return features.includes(ENTITLEMENT_ALL) || features.includes(required);
}

/** True when `features` grants every one of `required`. */
export function hasAllEntitlements(
  features: readonly Entitlement[] | null | undefined,
  required: readonly Entitlement[]
): boolean {
  return required.every(r => hasEntitlement(features, r));
}

/** True when `features` grants at least one of `required`. */
export function hasAnyEntitlement(
  features: readonly Entitlement[] | null | undefined,
  required: readonly Entitlement[]
): boolean {
  if (required.length === 0) return true;
  return required.some(r => hasEntitlement(features, r));
}

/** Default block written when an organization is created. */
export const DEFAULT_ENTITLEMENTS: OrganizationEntitlements = {
  plan: 'full',
  features: [ENTITLEMENT_ALL]
};

/* -------------------------------------------------------------------------- */
/*                            Platform billing                                */
/* -------------------------------------------------------------------------- */

/**
 * Subscription status, mirroring Stripe's own values.
 *
 * NOTE: this is the organization's subscription to **CarePortals**, billed on
 * the platform Stripe account. It is unrelated to the per-tenant Stripe
 * credentials in `StripeAdapter`, which charge the clinic's own patients.
 */
export type OrganizationBillingStatus =
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'unpaid'
  | 'incomplete'
  | 'incomplete_expired'
  | 'paused';

/**
 * Statuses that still carry access.
 *
 * `past_due` is deliberately included: a failed card must not take a clinic's
 * console down mid-consultation. Stripe keeps entitlements active through
 * dunning and only removes them once the subscription actually moves to
 * `canceled`/`unpaid`, at which point the entitlement webhook empties
 * `features` on its own. The UI shows a banner for `past_due` instead.
 */
export const BILLING_STATUSES_WITH_ACCESS: readonly OrganizationBillingStatus[] =
  ['trialing', 'active', 'past_due'];

/** True when the status should show a payment-problem banner. */
export function isBillingDelinquent(
  status: OrganizationBillingStatus | undefined
): boolean {
  return status === 'past_due' || status === 'unpaid';
}

/**
 * The **persisted** billing projection — deliberately minimal.
 *
 * Only what a request outside the billing page needs is stored here. Plan name,
 * period, price and product are display-only, read on one low-traffic
 * user-initiated page, and are fetched live from Stripe instead (see
 * `StripePlanDetails`). Storing them would buy nothing and add drift surface.
 *
 * `status` is the exception: it is persisted so a past-due banner can render
 * anywhere in the console straight from the session, without a Stripe call.
 */
export interface OrganizationBilling {
  /** Customer on the *platform* Stripe account. Absent = not yet onboarded. */
  stripeCustomerId?: string;
  /** Persisted so a delinquency banner can render console-wide. */
  status?: OrganizationBillingStatus;
  /** Last time a Stripe webhook or manual sync wrote this block. */
  syncedAt?: Date | string;
  /**
   * `created` of the Stripe event that last wrote this block. Ordering
   * watermark — see `OrganizationEntitlements.lastEventAt`.
   */
  lastEventAt?: Date | string;
}

/**
 * Plan detail read **live** from Stripe for the billing page. Never persisted.
 *
 * The billing page is user-initiated and low-traffic, so it can afford the
 * round-trip — and in exchange it always shows the truth rather than whatever
 * a webhook last managed to write.
 */
export interface StripePlanDetails {
  planName?: string;
  stripeProductId?: string;
  stripePriceId?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd?: boolean;
}
